import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../context/LangContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Home, Lock, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * Forgot-password flow (Gmail-verified reset).
 *
 * Step 1: User clicks "استعادة عبر Gmail" → we redirect them to Google Sign-In
 *         with a callback that returns them here with a `session_id` in the hash.
 * Step 2: On return, the page detects the session_id, and shows a "set new
 *         password" form. Submitting calls `/api/auth/reset-password-google`
 *         which verifies the Google session and updates the password of the
 *         account whose email matches the verified Gmail.
 */
export default function ForgotPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m) {
      setSessionId(decodeURIComponent(m[1]));
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const startGoogle = () => {
    const redirectUrl = window.location.origin + "/forgot-password";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) return toast.error(t("password_min_6"));
    if (newPassword !== confirm) return toast.error(t("passwords_do_not_match"));
    setSaving(true);
    try {
      await api.post("/auth/reset-password-google", {
        session_id: sessionId,
        new_password: newPassword,
      });
      toast.success(t("password_reset_success"));
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen grain-bg flex flex-col">
      <header className="max-w-7xl w-full mx-auto flex items-center justify-between px-4 py-4">
        <Link to="/" className="flex items-center gap-2 font-semibold text-[#2A5948]">
          <Home size={18} />
          <span>{t("brand")}</span>
        </Link>
        <LanguageSwitcher />
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white border border-[#E5E1D8] rounded-2xl p-8 shadow-sm" data-testid="forgot-password-page">
          {!sessionId ? (
            <>
              <h1 className="text-2xl font-semibold">{t("forgot_password_title")}</h1>
              <p className="text-sm text-[#5C6053] mt-1">{t("forgot_password_gmail_desc")}</p>
              <button
                data-testid="fp-google-btn"
                onClick={startGoogle}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 border border-[#E5E1D8] bg-white text-[#1C1E17] py-3 rounded-xl text-sm font-medium hover:bg-[#F3EFE7] transition-colors"
              >
                <GoogleLogo /> {t("reset_via_gmail")}
              </button>
              <div className="mt-3 flex items-center justify-center gap-2 text-xs text-[#5C6053]">
                <ShieldCheck size={12} /> {t("secured_by_google")}
              </div>
              <div className="mt-6 text-sm text-center text-[#5C6053]">
                <Link data-testid="fp-back-login" to="/login" className="text-[#2A5948] font-medium">
                  {t("back_to_login")}
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-[#E6F1E7] text-[#2A5948] flex items-center justify-center mx-auto"><CheckCircle2 size={26} /></div>
              <h1 className="text-2xl font-semibold text-center mt-4">{t("set_new_password")}</h1>
              <p className="text-sm text-[#5C6053] mt-1 text-center">{t("verified_via_gmail")}</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <Field label={t("new_password")} icon={Lock}>
                  <input data-testid="fp-new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="modal-input" minLength={6} required />
                </Field>
                <Field label={t("confirm_password")} icon={Lock}>
                  <input data-testid="fp-confirm-password-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="modal-input" minLength={6} required />
                </Field>
                <button
                  data-testid="fp-reset-btn"
                  type="submit"
                  disabled={saving}
                  className="w-full brand-btn py-3 rounded-xl font-medium disabled:opacity-60"
                >
                  {saving ? "..." : t("reset_password_cta")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>

      <style>{`.modal-input { width: 100%; border: 1px solid #E5E1D8; background: #F9F6F0; border-radius: 10px; padding: 10px 12px; font-size: 14px; outline: none; }
        .modal-input:focus { box-shadow: 0 0 0 2px rgba(42,89,72,0.25); border-color: #2A5948; }`}</style>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-[#5C6053] mb-1 flex items-center gap-1">
        {Icon && <Icon size={12} />} {label}
      </div>
      {children}
    </label>
  );
}

function GoogleLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
